import React, { forwardRef, ReactNode, useState } from "react";
import { cva, VariantProps } from "class-variance-authority";
import {
  applyCPFMask,
  applyPhoneMask,
  applyCEPMask,
  applyDateMask,
  applyDocumentMask,
} from "../../utils/maskUtils";
import {
  Container,
  HelperText,
  IconWrapper,
  InputContainer,
  InputField,
  Label,
} from "./Input.styles";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  colorLabel?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  rightIcon?: ReactNode;
  leftIcon?: ReactNode;
  width?: string;
  colorIcon?: string;
  name?: string;
  value?: string;
  type?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      className,
      placeholder,
      helperText,
      disabled,
      leftIcon,
      rightIcon,
      error,
      width,
      colorIcon,
      name,
      value,
      colorLabel,
      onChange,
      ...props
    },
    ref
  ) => {
    return (
      <Container>
        {!!label && <Label color={colorLabel}>{label}</Label>}
        <InputContainer style={{ width }}>
          {leftIcon && (
            <IconWrapper position="left">
              {React.cloneElement(leftIcon as React.ReactElement, {
                color: colorIcon || "",
                size: 24,
              })}
            </IconWrapper>
          )}
          <InputField
            ref={ref}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            borderColor={!error ? undefined : "#F3322B"}
            style={{
              width: "100%",
              padding: !leftIcon && !rightIcon ? "16px" : undefined,
              paddingLeft: leftIcon ? "42px" : undefined,
              paddingRight: rightIcon ? "42px" : undefined,
            }}
            {...props}
          />
          {rightIcon && (
            <IconWrapper position="right">
              {React.cloneElement(rightIcon as React.ReactElement, {
                color: colorIcon || "",
                size: 24,
              })}
            </IconWrapper>
          )}
        </InputContainer>
        {!!helperText && (
          <HelperText style={{ color: !error ? "#64748B" : "#F3322B" }}>
            {helperText}
          </HelperText>
        )}
      </Container>
    );
  }
);

export const CPFInput = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const [maskedValue, setMaskedValue] = useState(props.value || "");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyCPFMask(e.target.value);
      setMaskedValue(masked);
      props.onChange && props.onChange(e);
    };

    return (
      <Input
        {...props}
        value={maskedValue}
        onChange={handleChange}
        ref={ref}
      />
    );
  }
);

export const DocumentInput = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const [maskedValue, setMaskedValue] = useState(props.value || "");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyDocumentMask(e.target.value);
      setMaskedValue(masked);
      props.onChange && props.onChange(e);
    };

    return (
      <Input
        {...props}
        inputMode="numeric"
        value={maskedValue}
        onChange={handleChange}
        ref={ref}
      />
    );
  }
);

export const PhoneInput = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const [maskedValue, setMaskedValue] = useState(props.value || "");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyPhoneMask(e.target.value);
      setMaskedValue(masked);
      props.onChange && props.onChange(e);
    };

    return (
      <Input
        {...props}
        value={maskedValue}
        onChange={handleChange}
        ref={ref}
      />
    );
  }
);

export const CEPInput = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const [maskedValue, setMaskedValue] = useState(props.value || "");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyCEPMask(e.target.value);
      setMaskedValue(masked);
      props.onChange && props.onChange(e);
    };

    return (
      <Input
        {...props}
        value={maskedValue}
        onChange={handleChange}
        ref={ref}
      />
    );
  }
);

export const DateInput = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const [maskedValue, setMaskedValue] = useState(props.value || "");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyDateMask(e.target.value);
      setMaskedValue(masked);
      props.onChange && props.onChange(e);
    };

    return (
      <Input
        {...props}
        value={maskedValue}
        onChange={handleChange}
        ref={ref}
      />
    );
  }
);
